import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { addons } from "@/data";
import { startCheckoutItems } from "@/lib/api";

const num = (s) => parseInt(String(s).replace(/[^0-9]/g, ""), 10) || 0;
const buyable = addons.filter((a) => a.lookup);

const UpsellModal = ({ pkg, onClose }) => {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const toggle = (lookup) => setSelected((s) => (s.includes(lookup) ? s.filter((x) => x !== lookup) : [...s, lookup]));
  const total = num(pkg.price) + buyable.filter((a) => selected.includes(a.lookup)).reduce((sum, a) => sum + num(a.price), 0);

  const checkout = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Add a valid email so we can send your receipt and save your order.");
      return;
    }
    setLoading(true);
    const items = [{ lookup_key: pkg.lookup, quantity: 1 }, ...selected.map((l) => ({ lookup_key: l, quantity: 1 }))];
    try { await startCheckoutItems(items, email); }
    catch { toast.error("Couldn't start checkout, please try again."); setLoading(false); }
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="upsell-modal">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="relative w-full sm:max-w-lg bg-[#121212] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 md:p-8 max-h-[90vh] overflow-y-auto">
          <button onClick={onClose} className="absolute top-5 right-5 text-zinc-500 hover:text-white transition-colors" data-testid="upsell-close" aria-label="Close"><X size={22} /></button>
          <p className="text-[#ccff00] text-xs font-semibold uppercase tracking-widest mb-1">Your order</p>
          <h3 className="font-display font-black text-2xl mb-1">{pkg.name} <span className="text-zinc-500 text-lg">· from {pkg.price}</span></h3>
          <p className="text-zinc-400 text-sm mb-6">Add anything else you need and we'll do it all in one 48-hour launch.</p>

          <div className="space-y-2 mb-6">
            {buyable.map((a) => {
              const on = selected.includes(a.lookup);
              return (
                <button key={a.lookup} onClick={() => toggle(a.lookup)} className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 transition-colors ${on ? "border-[#ccff00] bg-[#ccff00]/10" : "border-white/10 bg-[#0a0a0a] hover:border-white/25"}`} data-testid={`upsell-addon-${a.lookup}`}>
                  <span className="flex items-center gap-3 text-sm text-white">
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center ${on ? "bg-[#ccff00] text-black" : "border border-white/25"}`}>{on ? <Check size={13} /> : <Plus size={13} className="text-zinc-500" />}</span>
                    {a.name}
                  </span>
                  <span className="font-display font-bold text-sm">{a.price}</span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mb-4">
            <span className="text-zinc-400">Total today</span>
            <span className="font-display font-black text-2xl" data-testid="upsell-total">£{total}</span>
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@business.co.uk"
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none mb-4"
            data-testid="upsell-email"
          />
          <button onClick={checkout} disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 hover:scale-[1.01] transition-transform disabled:opacity-70" data-testid="upsell-checkout">
            {loading ? <><Loader2 size={18} className="animate-spin" /> Redirecting…</> : "Continue to secure checkout"}
          </button>
          <p className="text-center text-xs text-zinc-600 mt-3">Starting prices · card payment via Stripe · promo codes at checkout</p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpsellModal;
